import { useState, useEffect } from 'react';
import type { Survey, Vessel } from '../types';
import { generateSurveyPDF, generateCargoReportPDF, downloadBlob } from '../lib/pdf-export';
import { calculateCargo } from '../lib/calculations';
import * as local from '../lib/db';
import { Save, Download, AlertTriangle, ChevronLeft, FileText, Weight, Package, Link2 } from 'lucide-react';

interface ResultsViewProps {
  vessel: Vessel;
  survey: Partial<Survey>;
  details: string[];
  warnings: string[];
  onSave: () => void;
  onBack: () => void;
}

export default function ResultsView({ vessel, survey, details, warnings, onSave, onBack }: ResultsViewProps) {
  const [linkedSurvey, setLinkedSurvey] = useState<Survey | null>(null);
  const [cargoResult, setCargoResult] = useState<{ cargoWeight: number; details: string[]; warnings: string[] } | null>(null);
  const [showCargo, setShowCargo] = useState(false);

  const handleExport = () => {
    const blob = generateSurveyPDF(vessel, survey as Survey, details, warnings);
    downloadBlob(blob, `Survey_${vessel.name}_${survey.survey_type}_${Date.now()}.pdf`);
  };

  const handleExportCargo = () => {
    if (!linkedSurvey || !cargoResult) return;
    const blob = generateCargoReportPDF(vessel, survey as Survey, linkedSurvey, cargoResult.cargoWeight, cargoResult.details, cargoResult.warnings);
    downloadBlob(blob, `Cargo_${vessel.name}_${Date.now()}.pdf`);
  };

  // Check for linked survey
  useEffect(() => {
    const findLinked = async () => {
      const allSurveys = await local.getSurveysLocal();
      const oppositeType = survey.survey_type === 'initial' ? 'final' : 'initial';
      const linked = allSurveys.find(s => 
        s.vessel_id === survey.vessel_id && 
        s.survey_type === oppositeType &&
        s.status === 'completed'
      );
      if (linked) {
        setLinkedSurvey(linked);
        try {
          const result = calculateCargo(
            survey.survey_type === 'initial' ? survey as Survey : linked,
            survey.survey_type === 'initial' ? linked : survey as Survey,
            vessel
          );
          setCargoResult(result);
        } catch (e) {
                  }
      }
    };
    findLinked();
  }, [survey.vessel_id, survey.survey_type]);

  const cargo = survey.cargo_weight ?? survey.displacement ?? survey.total_volume ?? 0;
  const isDisp = survey.method === 'displacement';
  const corr = survey.corrections;
  const isInitial = survey.survey_type === 'initial';

  return (
    <div className="space-y-6 animate-fade-in">
      <button type="button" onClick={onBack} className="flex items-center gap-2 text-sm text-navy-400 hover:text-navy-200 transition-colors">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      {/* Survey Type Badge */}
      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold ${isInitial ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'}`}>
        <FileText className="w-4 h-4" />
        {isInitial ? 'INITIAL SURVEY' : 'FINAL SURVEY'}
      </div>

      {warnings.length > 0 && (
        <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2 text-rose-400 font-bold text-sm">
            <AlertTriangle className="w-4 h-4" /> Warnings
          </div>
          {warnings.map((w, i) => (
            <p key={i} className="text-xs text-rose-300 pl-6">• {w}</p>
          ))}
        </div>
      )}

      {/* Main Result */}
      <div className="glass rounded-2xl p-6 text-center border-ocean-500/20">
        <p className="text-xs uppercase tracking-widest text-navy-400 mb-2">{survey.survey_type} Survey Result</p>
        <div className="text-4xl sm:text-5xl font-bold text-gradient font-mono tracking-tight">
          {typeof cargo === 'number' ? cargo.toFixed(3) : '0.000'}
        </div>
        <p className="text-sm text-ocean-400 mt-1 font-medium">Metric Tons (MT)</p>
      </div>

      {/* Linked Survey Alert */}
      {linkedSurvey && cargoResult && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 text-amber-400 font-bold text-sm mb-2">
            <Link2 className="w-4 h-4" /> {isInitial ? 'Final' : 'Initial'} Survey Found
          </div>
          <p className="text-xs text-amber-300 mb-3">
            {isInitial ? 'Final' : 'Initial'} survey completed on {new Date(linkedSurvey.survey_date).toLocaleDateString()}. 
            Cargo weight can be calculated.
          </p>
          <button type="button" 
            onClick={() => setShowCargo(!showCargo)}
            className="w-full py-2 bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 rounded-lg text-sm font-medium transition-colors"
          >
            {showCargo ? 'Hide' : 'Show'} Cargo Calculation
          </button>

          {showCargo && (
            <div className="mt-4 space-y-3">
              <div className="glass rounded-xl p-4 text-center border-amber-500/20">
                <p className="text-xs uppercase tracking-widest text-amber-400 mb-2">Cargo Weight</p>
                <div className="text-3xl font-bold text-amber-400 font-mono">
                  {cargoResult.cargoWeight.toFixed(3)}
                </div>
                <p className="text-xs text-amber-500 mt-1">Metric Tons (MT)</p>
              </div>
              <div className="space-y-1 font-mono text-xs text-navy-400">
                {cargoResult.details.map((d, i) => (
                  <div key={i} className={`py-1 px-2 rounded ${d.includes('CARGO') ? 'bg-amber-500/10 text-amber-300 font-bold' : ''}`}>
                    {d}
                  </div>
                ))}
              </div>
              <button type="button" onClick={handleExportCargo} className="w-full py-2.5 bg-amber-600 hover:bg-amber-500 text-white rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2">
                <Package className="w-4 h-4" /> Export Cargo Report
              </button>
            </div>
          )}
        </div>
      )}

      {/* Trim Corrections */}
      {isDisp && corr && (
        <div className="card-premium">
          <div className="p-4 border-b border-navy-700/50 flex items-center gap-2">
            <Weight className="w-4 h-4 text-ocean-400" />
            <h3 className="font-bold text-navy-200 text-sm">Trim Corrections (Metric Tons)</h3>
          </div>
          <div className="p-4 space-y-2 text-sm">
            <div className="flex justify-between text-navy-400">
              <span>Trim</span>
              <span className="text-navy-200 font-mono">{corr.trim.toFixed(3)} m</span>
            </div>
            <div className="flex justify-between text-navy-400">
              <span>List</span>
              <span className="text-navy-200 font-mono">{corr.list.toFixed(3)} m</span>
            </div>
            <div className="border-t border-navy-700/30 pt-2 flex justify-between items-center">
              <span className="text-navy-300 font-medium">1st Correction</span>
              <div className="text-right">
                <span className={`font-bold font-mono text-base ${corr.first_corr_mt >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {corr.first_corr_mt >= 0 ? '+' : ''}{corr.first_corr_mt.toFixed(2)} MT
                </span>
                <span className="block text-[10px] text-navy-500 font-mono">
                  ({corr.first_corr_m >= 0 ? '+' : ''}{corr.first_corr_m.toFixed(4)} m)
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-navy-300 font-medium">2nd Correction</span>
              <div className="text-right">
                <span className="font-bold font-mono text-base text-emerald-400">
                  +{corr.second_corr_mt.toFixed(2)} MT
                </span>
                <span className="block text-[10px] text-navy-500 font-mono">
                  (+{corr.second_corr_m.toFixed(4)} m)
                </span>
                <span className="block text-[10px] text-emerald-500/60 font-medium">ALWAYS ADD</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-navy-300 font-medium">List/Heel Corr</span>
              <div className="text-right">
                <span className="font-bold font-mono text-base text-emerald-400">
                  +{corr.list_corr_mt.toFixed(2)} MT
                </span>
                <span className="block text-[10px] text-emerald-500/60 font-medium">ALWAYS ADD</span>
              </div>
            </div>
            <div className="border-t border-navy-700/50 pt-2 flex justify-between font-bold text-navy-200">
              <span>Quarter Mean Draft</span>
              <span className="text-ocean-400 font-mono">{corr.quarter_mean.toFixed(3)} m</span>
            </div>
          </div>
        </div>
      )}

      {/* Calculation Log */}
      <div className="card-premium">
        <div className="p-4 border-b border-navy-700/50 flex items-center gap-2">
          <FileText className="w-4 h-4 text-navy-400" />
          <h3 className="font-bold text-navy-200 text-sm">Full Calculation Log</h3>
        </div>
        <div className="p-4 max-h-64 overflow-y-auto scrollbar-hide space-y-1 font-mono text-xs">
          {details.map((d, i) => (
            <div key={i} className={`py-1 px-2 rounded ${d.includes('TOTAL') || d.includes('CARGO') || d.includes('Final') || d.includes('Quarter Mean') || d.includes('ALWAYS') || d.includes('δMTC') ? 'bg-ocean-500/10 text-ocean-300 font-bold' : 'text-navy-400'}`}>
              {d}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button type="button" onClick={handleExport} className="btn-secondary flex items-center justify-center gap-2">
          <Download className="w-4 h-4" /> PDF
        </button>
        <button type="button" onClick={onSave} className="btn-primary flex items-center justify-center gap-2">
          <Save className="w-4 h-4" /> Save
        </button>
      </div>

      <div className="text-center">
        <p className="text-xs text-navy-500">
          {isDisp ? 'Displacement method' : survey.method === 'lpg' ? 'LPG method' : 'Ullage method'} • {vessel.name}
        </p>
      </div>
    </div>
  );
}
